import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ETypeOfNotification,
  Modal,
  RColumn,
  RemoteTable,
  RemoteTableRef,
  setNotification,
  stringFilterTemplate,
} from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect'
import { useDispatch } from 'react-redux'
import { ServiceListItemDTO, ServicesListQueryParams } from '../../dto/services/ServicesDTO'
import { useServicesService } from '../../servicesHooks/Services/useServicesService'
import LocationActionMenu, { LocationActionItem } from '../Location/tabs/components/LocationActionMenu'
import ServicesListCard, { ServicesCardActionsProps } from './components/ServicesListCard'
import ServicesFormView from './ServicesFormView'
import './Services.scss'

type UnknownRecord = Record<string, unknown>

interface FilterOption<TValue> {
  label: string
  value: TValue
}

const MOBILE_SEARCH_BATCH_SIZE = 200
const MOBILE_SEARCH_MAX_SCAN = 2000
const MOBILE_FILTER_DEBOUNCE_MS = 300

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

function normalizeFilterText(value: string | undefined | null) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

function matchesMobileFilter(item: ServiceListItemDTO, normalizedFilterText: string) {
  if (normalizedFilterText === '') return true

  const searchableValues = [
    item.externalCode,
    item.serviceName,
    item.currentBossName,
    item.active ? 'activo' : 'inactivo',
  ]

  return searchableValues.some((value) => normalizeFilterText(value).includes(normalizedFilterText))
}

export default function ServicesView() {
  const dispatch = useDispatch()
  const { deleteService, getServicesList } = useServicesService()
  const tableRef = useRef<RemoteTableRef>(null)
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null)
  const [activeFilter, setActiveFilter] = useState<boolean[]>([])
  const [mobileFilterDraft, setMobileFilterDraft] = useState('')
  const [mobileFilterText, setMobileFilterText] = useState('')
  const [deleteModal, setDeleteModal] = useState(false)
  const [serviceToDelete, setServiceToDelete] = useState<ServiceListItemDTO | null>(null)

  const activeOptions: Array<FilterOption<boolean>> = [
    { label: 'Activo', value: true },
    { label: 'Inactivo', value: false },
  ]

  function clearTableFiltersStorage() {
    const tableStateKeys = ['ServicesTable', 'mobileFilter_ServicesTable']
    tableStateKeys.forEach((key) => {
      localStorage.removeItem(key)
      sessionStorage.removeItem(key)
    })
  }

  useEffect(() => {
    clearTableFiltersStorage()
    return () => {
      clearTableFiltersStorage()
    }
  }, [])

  useEffect(() => {
    tableRef.current?.refreshTable?.()
  }, [activeFilter, mobileFilterText])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setMobileFilterText(mobileFilterDraft)
    }, MOBILE_FILTER_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [mobileFilterDraft])

  async function loadServices(offSet: number, pageSize: number, params: ServicesListQueryParams = {}) {
    const normalizedMobileFilterText = normalizeFilterText(mobileFilterText)

    if (normalizedMobileFilterText === '') {
      return getServicesList(offSet, pageSize, {
        ...params,
        activeFilter: activeFilter.length > 0 ? activeFilter : [],
      })
    }

    const fetchSize = Math.max(MOBILE_SEARCH_BATCH_SIZE, pageSize)
    let fetchOffset = 0
    let scannedItems = 0
    const requiredMatches = offSet + pageSize
    const filteredItems: ServiceListItemDTO[] = []

    while (scannedItems < MOBILE_SEARCH_MAX_SCAN && filteredItems.length < requiredMatches) {
      const batch = await getServicesList(fetchOffset, fetchSize, {
        ...params,
        activeFilter: activeFilter.length > 0 ? activeFilter : [],
        cardFilterDTO: null,
      })

      if (batch.length === 0) {
        break
      }

      scannedItems += batch.length
      filteredItems.push(...batch.filter((item) => matchesMobileFilter(item, normalizedMobileFilterText)))

      if (batch.length < fetchSize) {
        break
      }

      fetchOffset += fetchSize
    }

    return filteredItems.slice(offSet, offSet + pageSize)
  }

  function openEdit(item: ServiceListItemDTO) {
    setSelectedServiceId(item.serviceId)
  }

  function openDelete(item: ServiceListItemDTO) {
    setServiceToDelete(item)
    setDeleteModal(true)
  }

  async function confirmDelete() {
    if (!serviceToDelete) {
      setDeleteModal(false)
      return
    }

    try {
      await deleteService(serviceToDelete.serviceId)
      dispatch(setNotification({
        title: 'Servicio eliminado',
        desc: 'El servicio se eliminó correctamente.',
        typeOfNotification: ETypeOfNotification.INFO,
        icon: 'pi pi-check',
      }))
      tableRef.current?.refreshTable?.()
    } catch (error: unknown) {
      const response = isRecord(error) && isRecord(error.response) ? error.response : null
      const status = typeof response?.status === 'number' ? response.status : null

      dispatch(setNotification({
        title: status === 409 ? 'No se puede eliminar' : 'Error',
        desc: status === 409
          ? 'El servicio está siendo utilizado en otras relaciones.'
          : 'No se pudo eliminar el servicio.',
        typeOfNotification: status === 409 ? ETypeOfNotification.WARNING : ETypeOfNotification.ERROR,
        icon: status === 409 ? 'pi pi-exclamation-triangle' : 'pi pi-times',
      }))
    } finally {
      setDeleteModal(false)
      setServiceToDelete(null)
    }
  }

  function getFilterText(input: unknown): string {
    if (typeof input === 'string') return input
    if (!isRecord(input)) return ''
    const target = isRecord(input.target) ? input.target : null
    return typeof target?.value === 'string' ? target.value : ''
  }

  function handleMobileFilterChange(input: unknown) {
    setMobileFilterDraft(getFilterText(input))
  }

  const handleClearFilters = useMemo(() => () => {
    setActiveFilter([])
  }, [])

  function handleTableClearFiltersClick(event: React.MouseEvent<HTMLDivElement>) {
    const target = event.target
    if (!(target instanceof HTMLElement)) return

    const clearButton = target.closest('button[aria-label="Limpiar filtros"]')
    if (!clearButton) return

    clearTableFiltersStorage()
    window.setTimeout(() => {
      handleClearFilters()
    }, 0)
  }

  const cardActionsProps = useMemo<ServicesCardActionsProps>(() => ({
    onEdit: openEdit,
    onDelete: openDelete,
  }), [])

  const tableParams = useMemo(() => ({
    activeFilter: activeFilter.length > 0 ? activeFilter : [],
  }), [activeFilter])

  const tableRenderKey = useMemo(
    () => `services-table-${activeFilter.join('|')}`,
    [activeFilter],
  )

  const rowActions = useMemo(() => (rowData: ServiceListItemDTO) => {
    const menuItems: LocationActionItem[] = [
      {
        label: 'Editar',
        icon: 'pi pi-file-edit',
        action: () => openEdit(rowData),
      },
      {
        label: 'Eliminar',
        icon: 'pi pi-trash',
        action: () => openDelete(rowData),
      },
    ]

    return (
      <LocationActionMenu
        items={menuItems}
        ariaLabel='Opciones del servicio'
        buttonId={`servicesMenu_${rowData.serviceId}`}
        buttonClassName='p-button-text'
        panelClassName='services-action-menu-panel'
      />
    )
  }, [])

  const activeFilterElement = useMemo(() => () => (
    <MultiSelect
      options={activeOptions}
      optionLabel='label'
      optionValue='value'
      value={activeFilter}
      onChange={(e: MultiSelectChangeEvent) => setActiveFilter((e.value ?? []) as boolean[])}
      className='w-full services-filter-multiselect'
      style={{ height: '2.25rem', marginBottom: '0.28rem' }}
    />
  ), [activeFilter])

  if (selectedServiceId != null) {
    return (
      <ServicesFormView
        serviceId={selectedServiceId}
        setServiceId={setSelectedServiceId}
        onSaved={() => tableRef.current?.refreshTable?.()}
      />
    )
  }

  return (
    <div className='viewContainer services-view' onClick={handleTableClearFiltersClick}>
      <div className='services-header'>
        <h1 className='services-title'>Servicios</h1>
        <Button
          id='buttonCreateServiceId'
          type='button'
          className='buttonMain'
          label='Crear'
          icon='pi pi-plus-circle'
          onClick={() => {
            setSelectedServiceId(-1)
          }}
        />
      </div>
      <div className='services-divider' />

      <RemoteTable
        key={tableRenderKey}
        idTable='ServicesTable'
        dataKey='serviceId'
        className='services-table'
        getItems={loadServices}
        resizableColumns
        dynamicBehaviour
        CardTemplate={ServicesListCard}
        cardKeyName='serviceId'
        cardAdditionalProps={cardActionsProps}
        handleMobileFilter={handleMobileFilterChange}
        params={tableParams}
        ref={tableRef}
        filterRow
        rowActions={rowActions}
      >
        <RColumn field='externalCode' className='column' header='Código Externo' filter filterElement={stringFilterTemplate} />
        <RColumn field='serviceName' className='column' header='Nombre del servicio' filter filterElement={stringFilterTemplate} />
        <RColumn
          field='currentBossName'
          className='column'
          header='Jefe de servicio'
          body={(item: ServiceListItemDTO) => item.currentBossName || '-'}
          filter
          filterElement={stringFilterTemplate}
        />
        <RColumn
          field='active'
          className='column'
          header='Estado'
          body={(item: ServiceListItemDTO) => (item.active ? 'Activo' : 'Inactivo')}
          filter
          filterElement={activeFilterElement}
        />
      </RemoteTable>

      <Modal
        visible={deleteModal}
        setVisible={setDeleteModal}
        header='Eliminar servicio'
        footerButtonRightText='Eliminar'
        footerButtonLeftText='Cancelar'
        onClickRightBtn={() => { void confirmDelete() }}
        onClickLeftBtn={() => setDeleteModal(false)}
      >
        <p>
          ¿Querés eliminar el servicio <strong>{serviceToDelete?.serviceName ?? ''}</strong>?
        </p>
      </Modal>
    </div>
  )
}
